import React from 'react';
import { Box, IconButton, List, ListItem, SxProps } from '@mui/material';
import { Button } from 'system/atoms';
import FilterListIcon from '@mui/icons-material/FilterList';
import Popover from '@mui/material/Popover';
import { FilterOption } from './types';
import {
  deleteSearchParams,
  updateSearchParams
} from 'services/navigation/paths';
import { useSearchParams } from 'react-router-dom';
import { RadioItem } from 'common/ui/RadioItem';

const filterListContainer: SxProps = {
  backgroundColor: 'white',
  px: '15px',
  py: '20px',
  minWidth: 160,
  borderStyle: 'solid',
  borderColor: 'customGrey.medium',
  borderWidth: '1px'
};

const iconStyle: SxProps = {
  borderRadius: 1
};

const clearFilterButtonSx: SxProps = {
  p: 0,
  mt: 2
};

type Props = {
  onFilterSelect: (value: string | boolean) => void;
  filterOptions: FilterOption[];
  onRemoveFilters?: (filterId: string) => void;
  hideRemoveFilters?: boolean;
  filterId?: string;
  filterValue?: string | boolean;
};

const FiltersList = ({
  filterOptions,
  onFilterClick,
  filterValue
}: {
  filterOptions: FilterOption[];
  onFilterClick: (option: FilterOption) => void;
  filterValue: string | boolean | undefined;
}) => {
  return (
    <List sx={{ p: 0 }}>
      {filterOptions.map((option, index) => {
        const isLast = index === filterOptions.length - 1;
        const isSelected = option.value === filterValue;

        return (
          <ListItem
            key={String(option.value)}
            sx={{
              mb: isLast ? 0 : 1,
              p: 0
            }}
          >
            <RadioItem
              name={option.label}
              value={isSelected}
              onChange={() => onFilterClick(option)}
              label={option.label}
            />
          </ListItem>
        );
      })}
    </List>
  );
};

export const FilterButton: React.FC<Props> = ({
  onFilterSelect,
  filterOptions,
  onRemoveFilters,
  hideRemoveFilters,
  filterId,
  filterValue
}) => {
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen((prev) => !prev);
  };

  const onFilterClick = (option: FilterOption) => {
    onFilterSelect(option.value);
    setOpen(false);

    if (filterId) {
      updateSearchParams({
        searchParams,
        newParam: {
          key: filterId,
          value: String(option.value)
        },
        setSearchParams
      });
    }
  };

  const handleRemoveFilters = () => {
    setOpen(false);

    if (filterId) {
      onRemoveFilters?.(filterId);

      deleteSearchParams({
        searchParams,
        key: filterId,
        setSearchParams
      });
    }
  };

  return (
    <Box>
      <Popover
        sx={{
          zIndex: 2,
          maxHeight: '400px'
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        onClose={() => setOpen(false)}
      >
        <Box sx={filterListContainer}>
          <FiltersList
            filterOptions={filterOptions}
            onFilterClick={onFilterClick}
            filterValue={filterValue}
          />

          {onRemoveFilters && !hideRemoveFilters && (
            <Button
              variant="tertiary"
              sx={clearFilterButtonSx}
              onClick={handleRemoveFilters}
            >
              Clear filter
            </Button>
          )}
        </Box>
      </Popover>
      <Box position="relative">
        <IconButton onClick={handleClick} size="small">
          <FilterListIcon fontSize="small" sx={iconStyle} />
        </IconButton>
      </Box>
    </Box>
  );
};
